import React from 'react';
import { formatDateCompact } from '../utils/dateFormat';
import { isPlacedStatus } from '../constants/statuses';
// 🔀 **`formatDateCompact` WAS REMOVED 2026-08-08 AND RE-ADDED 2026-08-14, FOR A
// DIFFERENT TOOLTIP.** The owner had dates removed from the NOT-placed tooltip
// because the cell already shows both (see the builder's docblock). It is back
// ONLY for the PLACED branch, where the owner asked the green dot's hover to say
// *"Ordered on <date>"* (2026-08-14) — a self-contained summary on the dot. Do
// NOT print a date in the not-placed branch; that duplication is still deleted.
// 🟢 **UPDATED 2026-08-18 — also used by the TERMINAL branch** (delivered /
// partially_delivered / cancelled / rejected), see `buildOrderDateTooltip`'s
// docblock further down. These are NEW facts the row does not otherwise show,
// not a reinstatement of anything the 2026-08-08 trim removed.

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ARCHAEOLOGY — moved here 2026-09-13 from `client/src/components/CLAUDE.md`
 * ═══════════════════════════════════════════════════════════════════════
 * Root `CLAUDE.md`'s own rule: *"the RULE belongs here; the ARCHAEOLOGY belongs
 * in the docblock AT THE SITE — unbudgeted, beside the code, read by whoever is
 * about to change it."* That bullet was 5,500 bytes in a topic file with 757
 * bytes of headroom. The RULES stayed there as a short bullet ending
 * *→ archaeology in the file's docblock*; what follows is everything else,
 * VERBATIM.
 *
 * 🔑 **MOST OF THAT BULLET WAS ALREADY WRITTEN DOWN HERE, BETTER, AND WAS
 * THEREFORE DELETED RATHER THAN MOVED.** Each duplicate and the docblock that
 * already carried it, so nobody has to wonder whether something was lost:
 *   • the owner's *"yes colourd dots amber for slack between 3 and 5 days. red
 *     for <3 days"* and "he described the SHIPPED thresholds" — THE TABLE-LAYOUT
 *     DOT block above
 *   • the whole NO-GREEN-DOT ruling, its reasoning, and the §2 test cite — the
 *     same block, plus the `LEAD_TIME_DOT_CLASSES` note beneath it
 *   • `DueIndicator` is not reused, and the `role="img"` + `aria-label`
 *     contract — the same block
 *   • the four measured contrast ratios, 1.4.11, and the amber-600-not-500
 *     correction — the same block (which ALSO records that `index.css` was
 *     grepped for an `!important` on `background-color` first; the topic file
 *     never carried that half)
 *   • the dot's missing `title`, and why a child `title` shadows its parent's —
 *     the `NO title SINCE 2026-08-08` comment inside `LeadTimeDot`
 *   • the tooltip's two owner quotes, the two-line output, "only what the cell
 *     does not say", the no-tooltip branches, `=== 0`, the omitted lead-time
 *     line, the 2026-08-16 no-deadline update with its owner quote, the
 *     `showTimeCritical`-runs-first gate, and the `LeadTimeRisk.dateTooltip`
 *     test cite — THE ORDER TABLE'S DATE-CELL TOOLTIP block below
 *   • `expected_delivery_date` replacing the computed line, "reduce, not
 *     append", `received_by_names` and its `and N others` cap — the
 *     `expectedDeliveryDate` / `receivedByNames` params below
 *
 * ── MOVED VERBATIM (1): WHY THE DOT IS IN THE DATE CELL AND NOT IN STATUS ──
 * 🔀 **IT MOVED TO THE DATE CELL HOURS LATER, and the reason it was ever in
 * Status is the reason it could move.** It sat in the Status cell because the
 * Date column showed only `order_date` — the date the order was RAISED — so a
 * dot there would have been tied to the wrong date. That objection was right,
 * and the owner answered it directly: *"how about the date column for orders is
 * a two liner? top line is ordered date and bottom line is required by date?
 * then we can add the slack dot next to the bottom line date?"* The deadline
 * moved INTO the column, so the dot now sits beside the date it is about. See
 * the Date column bullet in the sanctioned-exception box
 * (`client/src/pages/CLAUDE.md`)
 *
 * ── MOVED VERBATIM (2): THE 2026-08-19 ETA ROUTE'S OWN GATE ──
 * Buyers now correct the ETA via `PATCH /:id/expected-delivery`
 * (`ordered`/`partially_delivered` only, `canManageDepartmentOrders`).
 * 🔑 The `expectedDeliveryDate` param below documents what the STORED date does
 * to this file's output; this is the half it does not carry — WHO may set one
 * and WHEN, which is why a not-placed order can never have one.
 *
 * ── MOVED VERBATIM (3): A CORRECTION RECORD ABOUT THE PROSE ITSELF ──
 * ⚠️ **This said "+ `title`" until 2026-08-08 and the dot has had NO `title`
 * since the unified Date-cell tooltip landed** — kept because a correction that
 * is gone reads exactly like a claim that was never wrong. The rule it corrects
 * is stated in full inside `LeadTimeDot`.
 */

/**
 * Days from today (local midnight) to `dateStr + days`, for the no-required-by
 * PLACED tooltip's "Expected in Y days" line — see `buildOrderDateTooltip`'s
 * 2026-08-16 docblock update for why this is a NEW computation rather than a
 * re-derivation of anything `computeSlackDetail` already returns.
 *
 * Same local-midnight idiom as `UrgencyBadge.js#computeDaysUntilDeadline`
 * (`T00:00:00` on parse, `setHours(0,0,0,0)` on "today") — never `toISOString()`,
 * which converts to UTC first and lands a day early throughout BST.
 *
 * @param {string} dateStr `YYYY-MM-DD` — the order's `order_date`.
 * @param {number} days    the stated lead time, already `== null`-checked by the caller.
 * @returns {number} signed day count; negative = in the past.
 */
function daysFromTodayLocal(dateStr, days) {
  const expected = new Date(dateStr + 'T00:00:00');
  expected.setDate(expected.getDate() + days);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((expected - today) / msPerDay);
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * THE ONE ANSWER TO "WHEN IS THIS PLACED ORDER EXPECTED?" — 2026-09-13.
 * ═══════════════════════════════════════════════════════════════════════
 * Signed days from today to the delivery expectation of a PLACED order.
 * Negative = the date has already passed.
 *
 * 🔑 **THE DOT AND THE CELL TOOLTIP MUST NEVER DISAGREE, and this function is
 * the mechanism** — not a convention. The owner's complaint on 2026-09-13 was
 * exactly a disagreement of that kind: a GREEN dot ("Order placed") beside a
 * tooltip reading *"Expected 28 days ago"*, which he called *"really
 * misleading"*. Two copies of this arithmetic would let the same pair drift
 * again the first time anyone retunes one of them, so `LeadTimeDot` and
 * `buildOrderDateTooltip` both read THIS — never their own subtraction.
 *
 * 🔀 **PRECEDENCE IS THE BUYER'S DATE FIRST**, matching the 2026-08-19 ruling
 * that a stored `expected_delivery_date` REPLACES (never accompanies) the
 * computed guess: a real ETA is a better answer to the same question, so a
 * buyer-set date in the past outranks a required-by date still in the future.
 * 🪤 The fallback is `slack + lead`, NOT `order_date + lead_time_days`: slack
 * already encodes days-from-today, and `computeSlackDetail` returns
 * `min_slack_lead_days` null together with `required_by_date` — see
 * `buildOrderDateTooltip`'s no-deadline branch, which is the ONE place that
 * legitimately uses the raw column instead, and which this function therefore
 * does not serve (`LeadTimeDot` cannot reach it: the dot renders only inside
 * the required-by line, so no deadline means no dot at all).
 *
 * @param {number|null} [args.slackDays] `order.min_slack_days`.
 * @param {number|null} [args.leadDays] `order.min_slack_lead_days`.
 * @param {string|null} [args.expectedDeliveryDate] the buyer-set `YYYY-MM-DD`.
 * @returns {number|null} signed day count, or null when no expectation is
 *   derivable — which callers must treat as "say nothing", never as "fine".
 */
export function deliveryOffsetDays({ slackDays = null, leadDays = null, expectedDeliveryDate = null } = {}) {
  if (expectedDeliveryDate) return daysFromTodayLocal(expectedDeliveryDate, 0);
  if (slackDays == null || leadDays == null) return null;
  return slackDays + leadDays;
}

/**
 * The single "Updated delivery date: <date> — <interval>" line for a
 * buyer-set `expected_delivery_date` — 2026-08-19, owner ruling: *"If the
 * delivery date has been updated we can remove original expected date and
 * have an 'updated delivery date xxxx expected in xx days' or something
 * similar"*.
 *
 * 🔑 **REDUCE, NOT GROW.** Once a real ETA is stored, the computed "Expected
 * in Y days" guess is not additional context beside it — it is a WORSE
 * answer to the SAME question. This function's caller REPLACES that line
 * with this one; it never appends. Same rule the terminal branch already
 * applies (a delivered/cancelled order drops the slack dot, the lead-time
 * line and the deadline entirely and says one thing instead) — extended
 * earlier in the lifecycle rather than invented twice.
 *
 * 🪤 **The past case is not optional — a stored date goes stale by simply
 * existing**, with no action from anyone, so an implementation that only
 * handles the future reads "expected in -4 days" the moment "today" passes
 * it. Reuses the SAME "overdue" notion as the extracted OVERDUE badge
 * (`components/ExpectedDeliveryBadge.js`) — a plain day-count comparison
 * against local midnight — rather than a second implementation of the idea.
 * `daysFromTodayLocal(dateStr, 0)` IS that comparison: days from today to
 * `dateStr` with nothing added, the same local-midnight idiom every other
 * date computation in this file uses (never `toISOString()`).
 */
function buildUpdatedDeliveryLine(expectedDeliveryDate) {
  // 🔑 2026-09-13 — via `deliveryOffsetDays`, not a second call to
  // `daysFromTodayLocal`, so the dot beside this line reads the SAME number.
  // The value is identical (that helper's buyer-date branch IS this call).
  const offsetDays = deliveryOffsetDays({ expectedDeliveryDate });
  const interval = offsetDays < 0
    ? `${Math.abs(offsetDays)} day${offsetDays === -1 ? '' : 's'} overdue`
    : offsetDays === 0
      ? 'expected today'
      : `expected in ${offsetDays} day${offsetDays === 1 ? '' : 's'}`;
  return `Updated delivery date: ${formatDateCompact(expectedDeliveryDate)} — ${interval}`;
}

/**
 * Pure band-selection logic. Returns `{ band }` (`risk` slack <= 2, `tight`
 * <= 5, `ok` above) for the given slack value, or null if slack is
 * null/undefined. `LeadTimeDot` is its only production reader.
 *
 * 🔻 The card pill component (default export), `formatSignedSlack` and the
 * `baseLabel` / `pillClasses` fields were DELETED 2026-10-18 with the order
 * cards (pre-deletion SHA 38200402).
 */
export function getLeadTimeBand(slackDays) {
  if (slackDays === null || slackDays === undefined) return null;
  if (slackDays <= 2) {
    return { band: 'risk' };
  }
  if (slackDays <= 5) {
    return { band: 'tight' };
  }
  return { band: 'ok' };
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * THE TABLE DOT — the only lead-time signal (the card pill was deleted 2026-10-18).
 * ═══════════════════════════════════════════════════════════════════════
 * Product owner, 2026-08-08: *"yes colourd dots amber for slack between 3 and 5
 * days. red for <3 days"*.
 *
 * 🔑 **HE DESCRIBED THE EXISTING THRESHOLDS, so nothing was redefined.** His
 * "<3" is `getLeadTimeBand`'s `slackDays <= 2` and his "3 to 5" is its `<= 5` —
 * independently arrived at, which is a good sign they are the right bands.
 * **This reads `getLeadTimeBand` and must never grow its own copy**: one band
 * function feeds every lead-time signal, or two of them drift the first time
 * anyone retunes a threshold (when the card pill existed there were two).
 *
 * 🔴 **NO GREEN DOT — a deliberate per-layout difference, confirmed by the owner
 * unprompted: *"no green dot"*. Do NOT "complete the set" for symmetry.** The
 * card layout's green "Lead time OK" pill went with the cards (2026-10-18). A
 * table flags PROBLEMS, and a column of green dots on a
 * twelve-column table is noise that makes the two colours that matter harder to
 * find. Pinned by a test asserting a comfortable order renders NOTHING, over the
 * same fixture an at-risk one uses.
 *
 * ♿ **Colour is not the only channel.** It carries `role="img"`, an `aria-label`
 * naming the state AND the number, and a `title` — the same contract
 * `components/DueIndicator.js` uses for the Breakdown's overdue dot.
 * ⚠️ **`DueIndicator` itself is deliberately NOT reused.** Its states are
 * `overdue` / `due_soon` — a DATE that has passed or is near. Lead-time slack
 * asks whether there is still time to PLACE the order. Adding these bands to it
 * would give one component five states from two unrelated domains and make its
 * name a lie. This is the second use of the DEVICE but the first of this
 * VOCABULARY; if a third dot ever appears, extract the primitive then.
 *
 * ♿ Contrast against the row background (`bg-white` / `dark:bg-gray-800`),
 * measured, with `index.css` grepped for `!important` on `background-color`
 * first (no rule targets any of these four classes):
 *   red-500  **3.76:1** light · red-400  **5.31:1** dark
 *   amber-600 **3.19:1** light · amber-400 **8.79:1** dark
 * All clear WCAG 1.4.11's 3:1, which applies because the dot is the SOLE
 * carrier here — there is no text beside it.
 * 🪤 The amber is **600, not 500**: amber-500 measures 2.15:1 on white and FAILS.
 * That was corrected app-wide earlier the same day; this inherits the fix rather
 * than re-introducing the failure.
 */
// 🔴 There is deliberately NO `ok` entry. Adding one and dropping the `ok`
// guard below is exactly the "complete the set" change the owner ruled against,
// and §2 of `OrderTableView.leadTimeDot.test.js` goes red on it — verified by
// doing it.
const LEAD_TIME_DOT_CLASSES = {
  risk: 'bg-red-500 dark:bg-red-400',
  tight: 'bg-amber-600 dark:bg-amber-400',
};

// 🟢 PLACED is a STATUS green, NOT the "comfortable slack" green the owner ruled
// against — that ruling STANDS (see §2 of `OrderTableView.leadTimeDot.test.js`
// and the shared-module bullet in CLAUDE.md). Owner, 2026-08-14: *"go for green
// for ordered orders. None still for large slack."* Green means the order has
// been PLACED (`ordered`/`partially_delivered`); it only ever REPLACES a
// risk/amber dot, because the `band.band === 'ok'` guard below is untouched — so
// a large-slack order stays dot-less whether or not it is placed.
// 🔻 **THAT LAST SENTENCE IS NOW TRUE ONLY OF THE GREEN, 2026-09-13.** A placed
// order whose DELIVERY is due within 2 days or already late takes red/amber from
// the delivery branch BEFORE the `ok` guard runs, at any placement band — see
// that branch's own note. The green itself is unchanged: it still never appears
// on a comfortable-slack row. `partially_delivered`
// counts as placed; `delivered`/`cancelled` never reach here (`showsTimeCriticalChrome`
// gates them out at the call site).
// ⚠️ Contrast is ESTIMATED, not measured — a browser check is owed like the
// red/amber figures above: green-600 ≈ 4.0:1 on white, green-400 ≈ 7.7:1 on
// gray-800, both clearing 1.4.11's 3:1 for a sole-carrier mark.
const PLACED_DOT_CLASS = 'bg-green-600 dark:bg-green-400';

// 🟠 "Expected today or within 2 days" — the owner's own number, 2026-09-13,
// INCLUSIVE at both ends (0 = today, 2 = the far end he named). Deliberately
// NOT derived from `getLeadTimeBand`'s 2/5 thresholds: those are about PLACING
// an order and share nothing with this but the digit.
const DELIVERY_DUE_SOON_DAYS = 2;

/** Human wording for the accessible name, per band. */
const LEAD_TIME_DOT_LABELS = {
  risk: 'Lead time risk',
  tight: 'Tight timeline',
};

export function LeadTimeDot({ slackDays, status, leadDays = null, expectedDeliveryDate = null }) {
  const band = getLeadTimeBand(slackDays);
  const placed = isPlacedStatus(status);

  // ══════════════════════════════════════════════════════════════════════
  // 🔴🟠 DELIVERY BAND — PLACED ORDERS ONLY. Owner ruling, 2026-09-13.
  // ══════════════════════════════════════════════════════════════════════
  // He saw a placed order carrying a GREEN dot whose cell tooltip read
  // *"Ordered on 27 Aug … Expected 28 days ago"* and called it *"really
  // misleading"*. The cause is that PLACEMENT slack keeps ticking down after
  // the order is placed, so every placed order eventually enters a risk/tight
  // band and the 2026-08-14 rule paints that dot green — a signal about an
  // action already taken, sitting where a signal about the DELIVERY should be.
  // His ruling, verbatim and EXACTLY two rows: *"just go for: Expected date
  // already passed → Red, tooltip 'Expected N days ago'; and Expected today or
  // within 2 days → Amber, on the To receive tab."*
  //
  // 🔑 **This runs BEFORE the `ok` guard, and that is the whole point.** Once
  // placed, the question the dot answers CHANGES from "should this have been
  // ordered by now?" to "should this have ARRIVED by now?", and an order with
  // comfortable PLACEMENT slack can still be a week late to arrive. So red and
  // amber here fire at any placement band, `ok` included.
  // 🔻 He did NOT adopt the other two rows he was offered — no green for
  // everything further out, and no "no dot without a date" — so anything past
  // 2 days falls through to the UNCHANGED behaviour below, green or nothing.
  // 🪤 **Gated on `band`, i.e. on `slackDays` being known.** With no slack
  // there is no required-by date, and the call site renders the dot only inside
  // the required-by line — so this branch would be unreachable anyway; the gate
  // makes that explicit rather than relying on the caller. An overdue buyer ETA
  // on an order with NO deadline therefore still shows no dot; it shows the
  // overdue wording in the cell tooltip, as it did before. Flagged to the owner
  // rather than decided here.
  const deliveryOffset = placed && band ? deliveryOffsetDays({ slackDays, leadDays, expectedDeliveryDate }) : null;
  if (deliveryOffset != null && deliveryOffset <= DELIVERY_DUE_SOON_DAYS) {
    const overdue = deliveryOffset < 0;
    const late = Math.abs(deliveryOffset);
    return (
      <span
        role="img"
        aria-label={overdue
          ? `Delivery overdue: ${late} day${late === 1 ? '' : 's'} past the expected date`
          : deliveryOffset === 0
            ? 'Delivery due: today'
            : `Delivery due: in ${deliveryOffset} day${deliveryOffset === 1 ? '' : 's'}`}
        className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${overdue ? LEAD_TIME_DOT_CLASSES.risk : LEAD_TIME_DOT_CLASSES.tight}`}
        data-testid="lead-time-dot"
        data-band={overdue ? 'delivery-overdue' : 'delivery-due'}
      />
    );
  }

  // No data, or the comfortable band — the table shows nothing. See the note
  // above: the absence at `ok` is the owner's explicit call, not an omission.
  // 🔑 This guard is what keeps "None still for large slack" true even once the
  // order is placed — a placed order with comfortable slack is `ok` and returns
  // null here, before the placed branch is ever reached.
  if (!band || band.band === 'ok') return null;

  const dotClass = placed ? PLACED_DOT_CLASS : LEAD_TIME_DOT_CLASSES[band.band];
  const dataBand = placed ? 'placed' : band.band;
  // 🔑 The NUMBER is in the accessible name, not just the state. "Lead time
  // risk: 2 days" tells a screen-reader or hover user what the colour cannot,
  // and is the whole reason this is not a bare coloured span. Once PLACED, the
  // slack count is moot — the buyer has acted — so the name says so instead.
  let label;
  if (placed) {
    label = 'Order placed';
  } else {
    const state = LEAD_TIME_DOT_LABELS[band.band];
    const detail = slackDays < 0
      ? `${Math.abs(slackDays)} day${slackDays === -1 ? '' : 's'} overdue — order should already be placed`
      : slackDays === 0
        ? 'no slack — order today to meet the deadline'
        : `${slackDays} day${slackDays === 1 ? '' : 's'} until the order must be placed`;
    label = `${state}: ${detail}`;
  }

  // 🔀 **NO `title` SINCE 2026-08-08, DELIBERATELY — and `role="img"` +
  // `aria-label` STAY.** The owner asked for one tooltip on the whole Date cell
  // (*"No need for two tool tips for each row just one tool tip give us all the
  // info we need"*), and a native `title` on a CHILD wins over its parent's — so
  // leaving it here would shadow the unified cell tooltip on exactly the pixels
  // a user aims at, which is the complaint rather than the fix.
  // ♿ **Check what the survivors are now solely responsible for** (the rule this
  // repo learned the hard way when a redundant dot became a sole carrier): the
  // MOUSE affordance moves to the `<td>`'s `buildOrderDateTooltip` string, which
  // says strictly more than this one did — it names the deadline and the lead
  // time as well as the state. The ASSISTIVE-TECH channel is untouched:
  // `aria-label` still carries the state AND the number, so colour is still not
  // the only channel. Nothing about the dot's contrast changed.
  // ⚠️ If a THIRD consumer ever renders this dot somewhere without a tooltip on
  // its container, it needs one — do not assume the cell will always be there.
  return (
    <span
      role="img"
      aria-label={label}
      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`}
      data-testid="lead-time-dot"
      data-band={dataBand}
    />
  );
}

/**
 * The terminal (finished-order) branch of `buildOrderDateTooltip` — owner
 * ruling 2026-08-18. Extracted to its own function because it answers a
 * completely different question from the live branches below it ("what
 * happened to this order" vs "what should the buyer do next") and because
 * `buildOrderDateTooltip` was already long enough that a fourth nested
 * `if (placed)` here would have buried it.
 *
 * @param {string|null} status the order's CURRENT status — read fresh every
 *   call, never cached, so an un-cancelled/resubmitted order falls through
 *   the `switch` below to `undefined` even if stale `cancellation`/
 *   `rejection` data were still passed in (defence in depth; the real
 *   guarantee is the caller clearing those fields, per the server helper).
 * @param {string|null} mostRecentReceivedDate `YYYY-MM-DD`, the latest
 *   `order_items.received_date` across the order — used for BOTH `delivered`
 *   and `partially_delivered` (the latter is handled by the caller, not
 *   here, since it is not a terminal status).
 * @param {string[]|null} [receivedByNames] `order.received_by_names`
 *   (`server/helpers/orderTerminalInfo.js`) — 2026-08-19 owner ruling,
 *   DISTINCT names of everyone who ticked ANY item received, capped and
 *   joined by `formatNameList`. Read ONLY on `delivered` (the owner's
 *   ruling named "delivered or cancelled"; `cancelled` already has its own
 *   single WHO via `buildWhoWhenReasonLine`, and `partially_delivered` is
 *   not terminal, so this parameter is not consulted there — flagged as a
 *   possible future extension, not built speculatively). `null`/empty is
 *   the COMMON case (`received_by` is NULL on every item of an order
 *   delivered before 2026-08-16) and must render the existing
 *   `Delivered on <date>` line byte-identically — see `formatNameList`.
 * @param {{date:string|null, by:string|null, reason:string|null}|null} cancellation
 * @param {{date:string|null, by:string|null, reason:string|null}|null} rejection
 * @returns {string|undefined}
 */
function buildTerminalOrderDateTooltip({ status, mostRecentReceivedDate, receivedByNames, cancellation, rejection }) {
  if (status === 'delivered') {
    if (!mostRecentReceivedDate) return undefined;
    const namesText = formatNameList(receivedByNames);
    return namesText
      ? `Delivered on ${formatDateCompact(mostRecentReceivedDate)} by ${namesText}`
      : `Delivered on ${formatDateCompact(mostRecentReceivedDate)}`;
  }

  if (status === 'cancelled') return buildWhoWhenReasonLine('Cancelled', cancellation);
  if (status === 'rejected') return buildWhoWhenReasonLine('Rejected', rejection);

  // No recognised terminal fact to report (an unrecognised/future status, or
  // this function reached with no data) — silence, never a half-built line.
  return undefined;
}

/**
 * `A, B, C` for ≤3 names; `A, B, C and N others` beyond that — 2026-08-19
 * owner ruling: *"if multiple people ticked off items just list the people
 * not which items they ticked off"*. A hover box is not a table, so an
 * unbounded list is capped rather than printed in full.
 *
 * 🪤 **`null`/`undefined`/`[]` all return `null` (never `''` or `'by '`)** —
 * `received_by` was only added 2026-08-16, so an order delivered before
 * that (the MAJORITY of the ~3,600 live orders) has NO names at all, and
 * that is the path this must get right FIRST: the caller's existing
 * "Delivered on <date>" line renders completely unchanged, with no trailing
 * "by", no dangling separator, no "by —".
 */
function formatNameList(names) {
  if (!names || names.length === 0) return null;
  if (names.length <= 3) return names.join(', ');
  const shown = names.slice(0, 3);
  const remaining = names.length - shown.length;
  return `${shown.join(', ')} and ${remaining} other${remaining === 1 ? '' : 's'}`;
}

/**
 * `<Verb> on <date> by <name>` then, only if present, `Reason: <reason>` on
 * its own line. Shared by the cancelled and rejected branches above — same
 * three facts (date / who / reason-if-any), same omission rule.
 *
 * 🪤 **A missing `date` returns `undefined` for the WHOLE thing, never a
 * dangling `on  by <name>`.** `date` is the anchor every real writer of a
 * cancellation/rejection supplies alongside an `order_audit_log` row in the
 * same request (see `server/helpers/orderTerminalInfo.js`); the only way to
 * reach here with no date is a tampered/inconsistent restore archive, and
 * silence is the honest answer to "what happened and when" when the "when"
 * is not known.
 */
function buildWhoWhenReasonLine(verb, info) {
  if (!info || !info.date) return undefined;
  const first = info.by
    ? `${verb} on ${formatDateCompact(info.date)} by ${info.by}`
    : `${verb} on ${formatDateCompact(info.date)}`;
  return info.reason ? `${first}\nReason: ${info.reason}` : first;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * THE ORDER TABLE'S DATE-CELL TOOLTIP — two lines, and ONLY what the cell
 * does not already say.
 * ═══════════════════════════════════════════════════════════════════════
 * Product owner, 2026-08-08, in two messages. First: *"on the date/slack colum
 * I want the tool tip updated to say 'Order within x days' when hovered over.
 * No need for two tool tips for each row just one tool tip give us all the info
 * we need"*. Then, looking at the result live in the browser: *"remove the date
 * it was ordered as thats already in the cell. show the order now/ orderin 3
 * days then put leadtime on the next line. no need for needed by date as thats
 * also in the main cell"*.
 *
 * The output is therefore exactly two lines, joined by a real `\n` (a native
 * `title` renders `\n` as a line break):
 *
 *     Order within 3 days
 *     5 day lead time
 *
 * 🔑 **THE RULE THE SECOND MESSAGE ESTABLISHED, AND IT IS THE PART THAT
 * GENERALISES: THE TOOLTIP SAYS ONLY WHAT THE CELL DOES NOT.** The Date column
 * is a TWO-LINE cell — `order_date` on top, `required_by_date` beneath with the
 * slack dot — so the first version printed both dates a second time, six inches
 * from where they already were, and buried the two facts that are *not* on the
 * row (the ACTION and the LEAD TIME) at the end of a long sentence. Before
 * adding anything here, ask whether the row already carries it.
 * ⚠️ **Consequence: this function no longer formats a date at all**, which is
 * why `utils/dateFormat` is not imported. The `orderDate` parameter is GONE —
 * do not reinstate it, and do not "restore" either date clause.
 *
 * 🔑 **`x` IS THE SLACK, NOT THE DAYS UNTIL THE DEADLINE — that is the whole
 * point of his phrasing.** `slack = daysUntilNeeded - leadDays`, i.e. how long
 * you can still wait before placing the order. Printing the gap to the deadline
 * where the slack belongs would tell a buyer they have three weeks on an order
 * that needed placing last Tuesday. Never re-derive either number here: both
 * arrive from `computeSlackDetail` on the server, captured in the one branch
 * that won the minimum, so the sentence and the dot beside it cannot disagree.
 *
 * 🔀 **IT REPLACED THREE NESTED `title` ATTRIBUTES, not two.** The cell had one
 * on the `<td>` (showing the **raw ISO string** `2026-04-25` — a real defect
 * nobody had reported), one on the required-by line, and one inside
 * `LeadTimeDot`. Native `title` resolves innermost-wins, so which of the three
 * you got depended on which pixel you were over. One tooltip on the `<td>` is
 * both the owner's request and the fix for the ISO leak. ⚠️ That fix survives
 * the trim: no date reaches this string in ANY form now, ISO or otherwise.
 *
 * 🚨 **`slackDays === 0` IS "Order today", AND IT MUST BE REACHED BY AN EXPLICIT
 * `=== 0`.** Owner's word, and the repo's most-repeated trap: `0` is a
 * legitimate value and can never double as the miss sentinel. A falsy test here
 * silently deletes the most urgent not-yet-overdue state on the page — the exact
 * shape that nearly sank the slack sort and that `leadTimeToDays` returns `null`
 * to avoid. 🪤 **The trim made this MORE fragile, not less**: with the date
 * clauses gone the miss branch now returns `undefined`, so a `!slackDays`
 * rewrite deletes the whole tooltip on a 0-slack order rather than merely
 * downgrading its wording. Pinned by §2 of `LeadTimeRisk.dateTooltip.test.js`.
 *
 * 🔻 **NEGATIVE SLACK PRINTS A MAGNITUDE, NEVER THE SIGNED VALUE.** "behind
 * schedule" already carries the direction, so `-4` beside it reads as "minus
 * four days behind" — a double negative. `Math.abs`, matching what
 * `LeadTimeRisk`'s own overdue tooltip has always done.
 *
 * 🔴 **A DELIVERED OR CANCELLED ORDER GETS NO TOOLTIP AT ALL — CHANGED
 * 2026-08-08, AND IT IS A JUDGEMENT CALL THIS TRIM CREATED RATHER THAN A RULE
 * BEING RELAXED.** The property is unchanged and still binding: **a finished
 * order never gets an instruction.** `showsTimeCriticalChrome` already hides the
 * dot on those orders because the warning is meaningless once the thing has
 * arrived, and leaving "Order within 3 days" here would put that live warning
 * straight back in through the door that replaced it. What changed is what is
 * LEFT once the instruction is withheld: the branch used to fall back to
 * `Ordered … · Needed by …`, and both of those dates are now deleted as
 * duplicates of the cell — so the fallback has nothing to fall back TO. An empty
 * string would render a blank popup box, so the honest answer is no attribute.
 * ⚠️ **Do not "restore" the facts-only form to give this branch something to
 * say** — that reinstates the duplication the owner asked to remove. If a
 * finished order ever needs a tooltip, it needs a fact that is NOT on the row.
 *
 * 🔴 **NO DEADLINE → NO TOOLTIP, for the same reason.** No deadline means no
 * slack, so there is no action to advise; the cell also renders no second line
 * and no dot, all three absent together because the server returns them
 * together. The order date used to carry this branch on its own and is now the
 * cell's business alone.
 * 🔑 **Returning `undefined` rather than `''` is load-bearing in both branches:**
 * `title={undefined}` renders NO attribute, while `title=""` renders one and
 * pops a blank tooltip box on hover.
 *
 * ⚠️ **`leadDays == null` means "not stated", and the LINE is then OMITTED
 * rather than hedged.** The server deliberately does not report
 * `computeSlackDetail`'s 1-day default (see its docblock): saying "1 day lead
 * time" on nearly every order would dress an assumption as data in the one
 * tooltip whose entire job is to explain where a number came from — and only 25
 * `order_items.lead_time` values are populated in the whole dev database, so
 * that is nearly every row, not an edge case. Silence beats a hedge nobody can
 * act on, and here it means a ONE-line tooltip. 🪤 `== null` and never
 * `!leadDays` — `0` is a real stated lead time and renders "same-day lead time".
 *
 * 🟢 **UPDATED 2026-08-14 — a PLACED order gets a DIFFERENT tooltip, and the
 * NOT-placed one gained a middle line.** Not placed: `Order within X` /
 * `Required in Y` / `Z day lead time` (Y = X + Z, the days until the deadline).
 * Placed (`ordered`/`partially_delivered`): `Ordered on <date>` /
 * `Expected in Y days`. The "no date in this tooltip" rule above is now
 * scoped to the NOT-placed branch — the placed branch prints the ordered date on
 * purpose (owner's request), which is why `formatDateCompact` is imported again.
 *
 * 🟢 **UPDATED 2026-08-16 — A ROW WITH NO REQUIRED-BY DATE NOW GETS A TOOLTIP
 * TOO, WHERE IT USED TO GET NONE.** Owner: *"orders with NO needed by date
 * should still have a tool tip on the date saying no required by date, lead
 * time xx expected in xx days or something similar to the other date
 * tool tips"*.
 *
 * 🚨 **`leadDays` (`min_slack_lead_days`) IS NOT USABLE HERE — IT IS NULL
 * TOGETHER WITH `slackDays` WHENEVER THERE IS NO DEADLINE.** `computeSlackDetail`
 * only ever populates its return trio (`slackDays`/`neededByDate`/`leadDays`)
 * from the SAME winning item, so a null `requiredByDate` means the server never
 * had a lead time to report either — see its docblock. The only honestly
 * available lead time in this state is the RAW order-level column,
 * `orders.lead_time_days`, which this function receives as `orderLeadTimeDays`
 * and reads ONLY inside the no-deadline branch. Never fall back to it when
 * `requiredByDate` is set — that would silently disagree with the winning
 * item's own lead time on the 747 legacy orders where the two differ.
 *
 * The two no-deadline cases, mirroring the wording already used below:
 *   - **Not placed**: `No required-by date` / `Z day lead time` (or the bare
 *     first line alone when no lead time is stated either — there is no date to
 *     anchor an "expected" line before the order has even been raised).
 *   - **Placed**: `No required-by date` / `Ordered on <date>` / `Expected in Y
 *     days`, Y computed CLIENT-SIDE from `orderDate + orderLeadTimeDays` against
 *     today — the one date-arithmetic exception in this file, and it is not a
 *     re-derivation of anything the server already computed (there is nothing
 *     to re-derive: `computeSlackDetail` returns nothing in this state). Same
 *     local-midnight technique `UrgencyBadge.js#computeDaysUntilDeadline` uses
 *     (`T00:00:00`, `setHours(0,0,0,0)`), so it cannot drift a day the way a
 *     `toISOString()` shortcut would.
 *
 * ⚠️ **Gated by `showTimeCritical` FIRST, same as every other branch** — a
 * delivered/cancelled order still gets no tooltip whether or not it ever had a
 * deadline, matching the existing "a finished order never gets an instruction"
 * rule rather than inventing a new one for this state.
 *
 * @param {Object}      args
 * @param {string|null} args.requiredByDate   server-computed deadline. Read as a
 *   PRESENCE test only in BOTH branches — the required-by date itself is never
 *   printed, because the cell already shows it.
 * @param {string|null} args.orderDate        the order's raise date, printed as
 *   `Ordered on <date>` in the PLACED branch only (null → that line is omitted).
 * @param {number|null} args.slackDays        server-computed `min_slack_days`
 * @param {number|null} args.leadDays         `min_slack_lead_days`; null = not stated
 * @param {boolean}     args.showTimeCritical `showsTimeCriticalChrome(order)`
 * @param {boolean}     args.placed           `isPlacedStatus(order.status)` — picks
 *   the ordered/expected-delivery wording over the order-within wording.
 * @param {number|null} [args.orderLeadTimeDays] `order.lead_time_days`, the RAW
 *   order-level column — consulted ONLY when `requiredByDate` is null (see
 *   above). `== null`, never falsy: `0` is a stated same-day lead time.
 * @param {string|null} [args.expectedDeliveryDate] `order.expected_delivery_date`
 *   — 2026-08-19, a BUYER-SET real ETA (`PATCH /api/orders/:id/expected-delivery`),
 *   distinct from every date above, which are all COMPUTED (`orderDate + lead
 *   time`, or `slack + lead`). **When present, it REPLACES the computed
 *   "Expected in Y days" / "Expected today" / "Expected N days ago" line in
 *   BOTH placed branches with ONE line from `buildUpdatedDeliveryLine`:
 *   `Updated delivery date: <date> — expected in Y days` (or `expected
 *   today` / `N days overdue`)** — the guess is superseded by a real value
 *   the moment a human enters one, and the line COUNT does not grow: one
 *   line out, one line in. 🔑 **This is the SAME "reduce, not append" rule
 *   the terminal branch already applies** (a delivered/cancelled order drops
 *   the slack dot, the lead-time line and the deadline outright and states
 *   one fact instead) — extended earlier in the lifecycle, not invented
 *   twice. See `buildUpdatedDeliveryLine`'s own doc for the overdue-handling
 *   detail (a stored date goes stale by simply existing, so past/today/
 *   future are all handled explicitly, reusing the same local-midnight
 *   "overdue" comparison `components/ExpectedDeliveryBadge.js` uses).
 *   Read ONLY in the two `placed` branches; a not-placed order cannot carry
 *   one anyway (the endpoint refuses before `ordered`). **Absent (the
 *   default, and the case for the ~3,600 orders with no manually-set date
 *   today), this function is BYTE-IDENTICAL to before this parameter
 *   existed** — every other line (`Ordered on`, `Partially delivered on`) is
 *   unaffected either way.
 *
 * 🟢 **UPDATED 2026-08-18 — a FINISHED order can now get a tooltip, owner
 * ruling.** The property directly above ("a finished order never gets an
 * INSTRUCTION") is unchanged and still gates the live slack/lead-time
 * machinery entirely out of a terminal order's tooltip — what changed is
 * what a terminal order is allowed to say INSTEAD, and only when it names a
 * fact that is not already on the row:
 *
 *   - **`delivered`**: `Delivered on <date>` — the date is the MOST RECENT
 *     `order_items.received_date` across the order, computed server-side
 *     (there is no order-level delivery date column), never `order_date`.
 *   - **`partially_delivered`**: this status is NOT in the "finished, no
 *     instruction" set — items are still outstanding, so it stays on the
 *     PLACED branch below and simply GAINS a `Partially delivered on <date>`
 *     line (same most-recent-received-date computation) between "Ordered on"
 *     and "Expected in Y days". Decision recorded at the call site: dropping
 *     "Expected" would delete live information about items still in transit,
 *     which the owner's wording never asked for.
 *   - **`cancelled`** / **`rejected`**: `Cancelled|Rejected on <date> by
 *     <name>`, then `Reason: <reason>` on its own line IF the reason is
 *     non-null — omitted cleanly, never a dangling "Reason:" or a trailing
 *     newline. **No slack dot, no lead-time line, no "order within"
 *     instruction** — owner: *"No need for slack dots, lead times etc on a
 *     cancled/rejected order."* This is why the terminal branch is checked
 *     BEFORE any of the slack/lead-time code below ever runs, structurally,
 *     not by omitting a line after computing it.
 *
 * 🔑 **WHO/WHEN COMES FROM THE SERVER, PRE-RESOLVED — this function does no
 * "latest wins" reconciliation of its own.** The server holds two records of
 * a rejection (`order_approval_steps`, silently STALE on the admin-override
 * status-jump path — see `server/helpers/orderTerminalInfo.js`'s docblock for
 * why `order_audit_log` is the canonical source) and, for both cancel and
 * reject, possibly MULTIPLE audit-log rows if the order went through more
 * than one cancel/resubmit or reject/resubmit cycle. The `cancellation` /
 * `rejection` objects this function receives are already the single, latest
 * answer; it renders them, and renders nothing when the object (or its
 * `date`) is absent — a defensive default, never a dangling "on  by " string.
 *
 * 🚨 **`status` FOLLOWS CURRENT STATUS ONLY, NEVER HISTORY.** A cancellation
 * can be UNDONE (`orders.status_before_cancel` + the status-jump route) — the
 * server clears `cancellation_reason` on the way out, and this function reads
 * `status`, not a "was ever cancelled" flag, so an un-cancelled order falls
 * straight through to the live branches below even if a stale `cancellation`
 * object were somehow still passed in.
 *
 * 🪤 **`order_audit_log.performed_by` (the `by` field) IS A DISPLAY-NAME
 * STRING, NEVER A USER ID** — fine here because this is DISPLAY ONLY. Never
 * let it reach a permission check or an identity comparison; see the
 * name-as-identity hazard class in `docs/ROLES.md` §I.
 *
 * @returns {string|undefined} Up to four `\n`-separated lines, or `undefined`
 *   when there is nothing to say, so the caller can spread it into `title=` and
 *   get NO attribute rather than an empty one that renders a blank tooltip box.
 */
export function buildOrderDateTooltip({
  requiredByDate,
  orderDate,
  slackDays,
  leadDays,
  showTimeCritical,
  placed = false,
  orderLeadTimeDays = null,
  status = null,
  mostRecentReceivedDate = null,
  receivedByNames = null,
  cancellation = null,
  rejection = null,
  expectedDeliveryDate = null,
}) {
  // A finished order gets no INSTRUCTION, deadline stated or not — but since
  // 2026-08-18 it may still get a FACT (see the docblock above). Checked
  // BEFORE anything else in this function, so a cancelled/rejected order can
  // never reach the slack/lead-time code below by any path — the owner's "no
  // slack dots, lead times etc" is structural, not a line omitted after being
  // computed.
  if (!showTimeCritical) return buildTerminalOrderDateTooltip({ status, mostRecentReceivedDate, receivedByNames, cancellation, rejection });

  if (!requiredByDate) {
    // NO DEADLINE. `computeSlackDetail` never populated `leadDays` either (see
    // the docblock above) — the only honest lead time here is the raw column.
    const leadClause = orderLeadTimeDays == null
      ? null
      : orderLeadTimeDays === 0 ? 'same-day lead time' : `${orderLeadTimeDays} day lead time`;

    if (placed) {
      const lines = ['No required-by date'];
      if (orderDate) lines.push(`Ordered on ${formatDateCompact(orderDate)}`);
      // 🟢 2026-08-18 — see the docblock above: partially_delivered stays on
      // this PLACED branch (it is not "finished") and GAINS this line rather
      // than losing "Expected" below. Gated on `status`, never merely on
      // `mostRecentReceivedDate` being present, so it cannot render for a
      // plain `ordered` order that happens to be handed a stray value.
      if (status === 'partially_delivered' && mostRecentReceivedDate) {
        lines.push(`Partially delivered on ${formatDateCompact(mostRecentReceivedDate)}`);
      }
      // 🔀 2026-08-19 — a buyer-set real ETA REPLACES the computed guess (see
      // `buildUpdatedDeliveryLine`'s own doc for why this REDUCES rather
      // than grows the tooltip, and is safe when absent).
      if (expectedDeliveryDate) {
        lines.push(buildUpdatedDeliveryLine(expectedDeliveryDate));
      } else if (orderDate && orderLeadTimeDays != null) {
        const expectedDays = daysFromTodayLocal(orderDate, orderLeadTimeDays);
        lines.push(expectedDays < 0
          ? `Expected ${Math.abs(expectedDays)} day${expectedDays === -1 ? '' : 's'} ago`
          : expectedDays === 0
            ? 'Expected today'
            : `Expected in ${expectedDays} day${expectedDays === 1 ? '' : 's'}`);
      }
      return lines.join('\n');
    }

    // NOT PLACED — no date to anchor an "expected" line against, so the lead
    // time (if stated) stands alone.
    return leadClause ? `No required-by date\n${leadClause}` : 'No required-by date';
  }

  // A deadline with no slack is unreachable from the real payload — see the
  // docblock — kept as a guard rather than assumed. 🪤 `slackDays == null`,
  // never `!slackDays`: 0 is "Order today".
  if (slackDays == null) return undefined;

  // `slack + lead` is the days from TODAY until the required-by date — slack
  // already encodes days-from-today, so no "today" is needed here and the number
  // cannot disagree with the dot beside it. Null lead → we cannot derive it, so
  // the dependent line is omitted (same rule as the lead-time line below).
  // 🔑 2026-09-13 — `deliveryOffsetDays({ slackDays, leadDays })`, called
  // WITHOUT the buyer date (the placed branch below applies that override
  // itself, and the NOT-placed lines below must keep counting to the DEADLINE,
  // which a buyer's ETA does not move). `slackDays` is non-null past the guard
  // above, so this is byte-for-byte the old `leadDays == null ? null : sum`.
  const dueInDays = deliveryOffsetDays({ slackDays, leadDays });
  const dueInPhrase = (verb) => dueInDays < 0
    ? `${verb} ${Math.abs(dueInDays)} day${dueInDays === -1 ? '' : 's'} ago`
    : dueInDays === 0
      ? `${verb} today`
      : `${verb} in ${dueInDays} day${dueInDays === 1 ? '' : 's'}`;

  // 🟢 PLACED — owner ruling 2026-08-14: the order has been made, so "order
  // within X" is not a live instruction. The green dot's hover confirms WHEN it
  // was ordered and WHEN to expect it. `Ordered on <date>` duplicates the cell's
  // top line deliberately, so the hover reads as a self-contained summary.
  // "Expected …" uses the days-until-required-by number (`slack + lead`).
  if (placed) {
    const lines = [];
    if (orderDate) lines.push(`Ordered on ${formatDateCompact(orderDate)}`);
    // 🟢 2026-08-18 — same line, same gate, as the no-deadline PLACED branch
    // above; kept between "Ordered on" and "Expected in" in both branches so
    // the reading order is always raise → partial arrival → what's still due.
    if (status === 'partially_delivered' && mostRecentReceivedDate) {
      lines.push(`Partially delivered on ${formatDateCompact(mostRecentReceivedDate)}`);
    }
    // 🔀 2026-08-19 — same override as the no-deadline branch above: a
    // buyer-set real ETA replaces the computed "Expected …" guess.
    if (expectedDeliveryDate) {
      lines.push(buildUpdatedDeliveryLine(expectedDeliveryDate));
    } else if (dueInDays != null) {
      lines.push(dueInDays < 0
        ? `Expected ${Math.abs(dueInDays)} day${dueInDays === -1 ? '' : 's'} ago`
        : dueInDays === 0
          ? 'Expected today'
          : `Expected in ${dueInDays} day${dueInDays === 1 ? '' : 's'}`);
    }
    return lines.length ? lines.join('\n') : undefined;
  }

  // NOT PLACED — the order still needs raising.
  // LINE 1 — THE ACTION. One voice across every state ("Order within…" / "Order
  // today" / "Order now…") so a user scanning tooltips never re-parses.
  // 🔀 **"APPROVE AND ORDER" WHILE `submitted` — owner ruling, 2026-09-13.** A
  // submitted order cannot be placed at all until someone approves it, so the
  // bare "Order within 3 days" named only the SECOND half of the work and read
  // as an instruction the viewer could not act on. `approved` keeps the original
  // three phrasings BYTE-IDENTICAL — the approval has happened, so naming it
  // again would be noise. 🔑 The deadline itself does not move: the number, LINE
  // 2 (`Required …`) and LINE 3 (the lead time) are untouched, and so is the
  // DOT's `aria-label` ("…until the order must be placed"), which he did not
  // rule on — this is the tooltip's wording alone.
  const verb = status === 'submitted' ? 'Approve and order' : 'Order';
  let action;
  if (slackDays < 0) {
    const late = Math.abs(slackDays);
    action = `${verb} now (${late} day${late === 1 ? '' : 's'} behind schedule)`;
  } else if (slackDays === 0) {
    action = `${verb} today`;
  } else {
    action = `${verb} within ${slackDays} day${slackDays === 1 ? '' : 's'}`;
  }

  // Lead time not stated → no LINE 2 and no LINE 3 (they both derive from it).
  if (leadDays == null) return action;

  // LINE 2 (NEW, owner 2026-08-14) — WHEN IT IS NEEDED. `Required in Y days`,
  // Y = days until the required-by date. The deadline countdown the buyer asked
  // to see beside the "when to order" one.
  const required = dueInPhrase('Required');

  // LINE 3 — THE LEAD TIME. Wording unchanged: "5 day lead time" is adjectival
  // and stays singular, and a stated 0 is "same-day lead time".
  const lead = leadDays === 0 ? 'same-day lead time' : `${leadDays} day lead time`;
  return `${action}\n${required}\n${lead}`;
}
